import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getCallExperienceCompanies,
  getCompanySettingsSummary,
  getGlobalCallSettings,
  getJourneys,
  getContentLibrary,
  getJourney,
  getContentDetail,
  saveContent,
  removeContentOverride,
  saveTemplateVariant,
  removeTemplateVariant,
  setToggle,
  getInboundTest,
  searchTestPatients,
  getRecentTestCalls,
  getTestCallInfo,
  getCallErrorHistory,
} from '@services/api';

// React Query hooks for the Call Experience tab. `companyId` null/'' = global default.

const toLanguage = (lang) => (lang === 'es' ? 'Spanish' : 'English');
const idOrNull = (id) => (id === '' || id === undefined ? null : id);

export function useCxCompanies() {
  return useQuery({ queryKey: ['cxCompanies'], queryFn: getCallExperienceCompanies, staleTime: 60_000 });
}

export function useCxJourneys() {
  return useQuery({ queryKey: ['cxJourneys'], queryFn: getJourneys, staleTime: Infinity });
}

export function useCxJourney(journeyId, productLineId, companyId, lang) {
  const company = idOrNull(companyId);
  return useQuery({
    queryKey: ['cxJourney', journeyId, productLineId ?? null, company, lang],
    queryFn: () => getJourney({ journeyId, productLineId, companyId: company, language: toLanguage(lang) }),
    enabled: !!journeyId,
    placeholderData: keepPreviousData,
  });
}

export function useCxSettings(companyId) {
  const company = idOrNull(companyId);
  return useQuery({
    queryKey: ['cxSettings', company],
    queryFn: () => getCompanySettingsSummary(company),
    enabled: company !== null,
    staleTime: 60_000,
  });
}

/** Numbers to dial, their Twilio routing and whether a call now reaches the queue. Refetched each time the panel opens. */
export function useCxInboundTest(companyId, enabled) {
  return useQuery({
    queryKey: ['cxInboundTest', companyId],
    queryFn: () => getInboundTest(companyId),
    enabled: !!companyId && enabled,
    staleTime: 0,
  });
}

/** The company's patients matching a name, account number or phone (2+ characters). */
export function useCxTestPatients(companyId, query) {
  const q = (query || '').trim();
  return useQuery({
    queryKey: ['cxTestPatients', companyId, q],
    queryFn: () => searchTestPatients(companyId, q),
    enabled: !!companyId && q.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** The company's phone calls today, newest first. */
export function useCxRecentTestCalls(companyId) {
  return useQuery({
    queryKey: ['cxRecentTestCalls', companyId],
    queryFn: () => getRecentTestCalls(companyId),
    enabled: !!companyId,
    staleTime: 0,
  });
}

/**
 * Contact record, Twilio legs, IVR log/answers/order, and logged phone errors for one call.
 * `target` is { contactId } or { callSid }; null fetches nothing.
 */
export function useCxTestCallInfo(companyId, target) {
  const key = target?.contactId ?? target?.callSid ?? null;
  const info = useQuery({
    queryKey: ['cxTestCallInfo', companyId, key],
    queryFn: () => getTestCallInfo(companyId, target),
    enabled: !!companyId && !!key,
    staleTime: 0,
    retry: false,
  });
  const callSid = info.data?.callSid || target?.callSid;
  const errors = useQuery({
    queryKey: ['cxTestCallErrors', callSid],
    queryFn: () => getCallErrorHistory(callSid),
    enabled: !!callSid,
    staleTime: 0,
  });
  return { info, errors };
}

export function useCxGlobalSettings() {
  return useQuery({ queryKey: ['cxGlobalSettings'], queryFn: getGlobalCallSettings, staleTime: 60_000 });
}

export function useCxContent(companyId, lang) {
  const company = idOrNull(companyId);
  return useQuery({
    queryKey: ['cxContent', company, lang],
    queryFn: () => getContentLibrary({ companyId: company, language: toLanguage(lang) }),
    placeholderData: keepPreviousData,
  });
}

export function useCxContentDetail(line, companyId) {
  const company = idOrNull(companyId);
  return useQuery({
    queryKey: ['cxContentDetail', line?.kind, line?.key, line?.productLineId ?? null, company],
    queryFn: () => getContentDetail({ kind: line.kind, key: line.key, productLineId: line.productLineId, companyId: company }),
    enabled: !!line,
  });
}

/** Every Call Experience write; each refreshes the picker, journeys, library and drawers. */
export function useCxMutations() {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    ['cxCompanies', 'cxJourney', 'cxContent', 'cxContentDetail', 'cxSettings'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }));
  };

  return {
    save: useMutation({ mutationFn: saveContent, onSuccess }),
    remove: useMutation({ mutationFn: removeContentOverride, onSuccess }),
    saveVariant: useMutation({ mutationFn: saveTemplateVariant, onSuccess }),
    removeVariant: useMutation({ mutationFn: removeTemplateVariant, onSuccess }),
    toggle: useMutation({ mutationFn: setToggle, onSuccess }),
  };
}
